import styled from 'styled-components';
import React, { ReactNode } from 'react';
import { color, space, font, query } from '@design/templateFns';

export interface PaginationProps {
  totalPages: number;
  currentPage: number;
  pageSelected: (selected) => void;
  pageBufferCount?: number;
}

export interface PaginationButtonProps {
  onClickFunction: (selected) => null;
  clickable: boolean;
  direction: string;
}

const ChevronButton = ({ direction, onClick, clickable }) => {
  return (
    <Button onClick={onClick} $clickable={clickable} disabled={!clickable} $direction={direction}>
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 14 14"
        xmlSpace="preserve"
      >
        <path
          d={direction === 'left' ? 'M10,13L4,7l6-6' : 'M4,1l6,6l-6,6'}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    </Button>
  );
};

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
`;

const PageElement = styled.button<{ $clickable?: boolean }>`
  ${font('body-lg')};
  color: ${color('gray4')};
  line-height: 1;
  text-align: center;
  padding: 0;
  background: none;
  border: none;
  outline: none;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};

  &:hover {
    color: ${color('gray5')};
  }

  & + & {
    margin-left: ${space('xl')};
  }
`;

const Button = styled.button<{ $direction: string; $clickable: boolean }>`
  margin-left: ${({ $direction }) => ($direction === 'left' ? '0' : '37.5px')};
  margin-right: ${({ $direction }) => ($direction === 'left' ? '37.5px' : '0')};
  color: ${color('gray4')};
  width: 0.9rem;
  height: 0.9rem;
  padding: 0;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;

  &:hover {
    color: ${color('gray5')};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${color('gray2')};
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  @media screen and (${query.atLeast('tablet')}) {
    margin-left: ${({ $direction }) => ($direction === 'left' ? '0' : '27.5px')};
    margin-right: ${({ $direction }) => ($direction === 'left' ? '27.5px' : '0')};
  }
`;

const PageSelectedElement = styled(PageElement)`
  color: ${color('fg')} !important;
  font-weight: 700;
`;

const noOp = () => {};

export const Pagination = ({
  totalPages,
  currentPage,
  pageBufferCount = 2,
  pageSelected,
}: PaginationProps): ReactNode => {
  const navForward = () => {
    if (currentPage < totalPages) {
      pageSelected(currentPage + 1);
    }
  };

  const navBackward = () => {
    if (currentPage > 1) {
      pageSelected(currentPage - 1);
    }
  };

  const getPagerCellElement = (text, isCurrent, clickable) => {
    return isCurrent ? (
      <PageSelectedElement key={text}>{text}</PageSelectedElement>
    ) : (
      <PageElement
        key={text}
        onClick={(e) => (clickable ? pageSelected(text) : noOp())}
        $clickable={clickable}
      >
        {text}
      </PageElement>
    );
  };

  const generatePagerCells = ({
    totalPages,
    currentPage,
    pageBufferCount = 2,
  }: PaginationProps) => {
    const cells = Array();

    if (currentPage > pageBufferCount + 1 && totalPages > pageBufferCount + 1) {
      cells.push(getPagerCellElement(1, false, true));
      cells.push(getPagerCellElement('...', false, false));
    }

    for (
      let index = Math.max(currentPage - pageBufferCount, 1);
      index <= currentPage + pageBufferCount && index <= totalPages;
      index++
    ) {
      cells.push(getPagerCellElement(index, index == currentPage, true));
    }

    if (currentPage < totalPages - pageBufferCount && totalPages > pageBufferCount + 1) {
      cells.push(getPagerCellElement('...', false, false));
      cells.push(getPagerCellElement(totalPages, false, true));
    }

    return cells;
  };

  const pages = generatePagerCells({
    totalPages,
    currentPage,
    pageBufferCount,
    pageSelected,
  });

  return (
    <PaginationContainer key={currentPage}>
      <ChevronButton direction="left" onClick={(e) => navBackward()} clickable={currentPage > 1} />
      {pages}
      <ChevronButton
        direction="right"
        onClick={(e) => navForward()}
        clickable={currentPage < totalPages}
      />
    </PaginationContainer>
  );
};
